import _ from 'lodash';
import React from 'react';
import {
  boolean, number, object, string,
} from 'yup';

import FormGroup from '~/commons/components/FormGroup.jsx';
import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';

import qs from 'qs';
import { useLocation } from 'react-router-dom';
import FormResource from './FormResource.jsx';

const defaults = {
  active: true,
  name: '',
  order: 0,
  mask: null,
  bgImg: null,
  measure: null,
  focusX: 0,
  focusY: 0,
  cameraHeight: 0,
  refDistance: 0,
  refY: 0,
};

const schema = object({
  active: boolean(),
  name: string().required(),
  order: number(),
  mask: string().required(),
  bgImg: string().required(),
  measure: string().required(),
  focusX: number(),
  focusY: number(),
  cameraHeight: number(),
  refDistance: number(),
  refY: number(),
});

const useResize = () => {
  const ref = React.useRef();
  const [layout, setLayout] = React.useState({ width: 0, height: 0 });
  React.useEffect(() => {
    const elem = ref.current;
    if (!elem) return undefined;
    setLayout({
      width: elem.clientWidth,
      height: elem.clientHeight,
    });
    const resizeObserver = new ResizeObserver(() => {
      setLayout({
        width: elem.clientWidth,
        height: elem.clientHeight,
      });
    });
    resizeObserver.observe(elem);
    return () => {
      resizeObserver.unobserve(elem);
    };
  }, []);
  return { ref, layout };
};

const VanishingPointView = ({ values, setValues }) => {
  const { focusX, focusY } = values;
  const { ref: containerRef, layout } = useResize();
  const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
  const scale = imageSize.width && layout.width ? imageSize.width / layout.width : 1;
  const posX = (Number(focusX) || 0) + 0.5 * imageSize.width;
  const posY = (Number(focusY) || 0) + 0.5 * imageSize.height;
  return (
    <div
      ref={containerRef}
      className='position-relative'
      onClick={(e) => {
        const { offsetX, offsetY } = e.nativeEvent;
        if (imageSize.width && layout.width) {
          setValues((v) => ({
            ...v,
            focusX: offsetX * scale - 0.5 * imageSize.width,
            focusY: offsetY * scale - 0.5 * imageSize.height,
          }));
        }
      }}
    >
      <img className='w-100' src={values.bgImg} onLoad={(e) => {
        const elem = e.nativeEvent.target;
        setImageSize({ width: elem.naturalWidth, height: elem.naturalHeight });
      }} />
      <img className='position-absolute w-100' src={values.measure} style={{ inset: 0 }} />
      <svg
        className='position-absolute'
        width='100%'
        height='100%'
        style={{ inset: 0 }}
        viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
      >
        <rect
          x={posX - scale}
          y={posY - 10 * scale}
          width={2 * scale}
          height={20 * scale}
          fill='white' />
        <rect
          x={posX - 10 * scale}
          y={posY - scale}
          width={20 * scale}
          height={2 * scale}
          fill='white' />
        <rect
          x={posX - 0.5 * scale}
          y={posY - 10 * scale}
          width={scale}
          height={20 * scale}
          fill='#00FF00' />
        <rect
          x={posX - 10 * scale}
          y={posY - 0.5 * scale}
          width={20 * scale}
          height={scale}
          fill='#00FF00' />
      </svg>
    </div>
  );
};

const ReferenceView = ({ values, setValues }) => {
  const { refY } = values;
  const { ref: containerRef, layout } = useResize();
  const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
  const scale = imageSize.width && layout.width ? imageSize.width / layout.width : 1;
  const posY = (Number(refY) || 0) + 0.5 * imageSize.height;
  return (
    <div
      ref={containerRef}
      className='position-relative'
      onClick={(e) => {
        const { offsetY } = e.nativeEvent;
        if (imageSize.width && layout.width) {
          setValues((v) => ({
            ...v,
            refY: offsetY * scale - 0.5 * imageSize.height,
          }));
        }
      }}
    >
      <img className='w-100' src={values.bgImg} onLoad={(e) => {
        const elem = e.nativeEvent.target;
        setImageSize({ width: elem.naturalWidth, height: elem.naturalHeight });
      }} />
      <img className='position-absolute w-100' src={values.measure} style={{ inset: 0 }} />
      <svg
        className='position-absolute'
        width='100%'
        height='100%'
        style={{ inset: 0 }}
        viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
      >
        <rect
          x={0}
          y={posY - scale}
          width={imageSize.width}
          height={2 * scale}
          fill='white' />
        <rect
          x={0}
          y={posY - 0.5 * scale}
          width={imageSize.width}
          height={scale}
          fill='#00FF00' />
      </svg>
    </div>
  );
}

const FormArScene = ({ doc }) => {
  const { search } = useLocation();
  const { tab } = qs.parse(search, { ignoreQueryPrefix: true }) ?? {};
  return (
    <FormResource
      {...{
        defaults,
        stored: doc,
        schema,
        api: '/api/core/arScenes',
        onSubmit: (values) => ({
          ...values,
        }),
      }}>
      {({ values, setValues }) => (
        <Tabs defaultActiveKey={tab ?? 'basic'} unmountOnExit>
          <Tab eventKey="basic" title="Basic">
            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">BASIC</h6>
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <label>Active</label>
                  <FormCheckGroup name="active" type="checkbox" label="" asSwitch />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="name" />
              </div>
              <div className="col">
                <FormGroup name="order" type="number" />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup
                  name="mask"
                  as="coverImg"
                  api="/api/core/arScenes/files"
                  label="Mask Image"
                />
              </div>
              <div className="col">
                <FormGroup
                  name="bgImg"
                  as="coverImg"
                  api="/api/core/arScenes/files"
                  label="Background Image"
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup
                  name="measure"
                  as="coverImg"
                  api="/api/core/arScenes/files"
                  label="Measure Image"
                />
              </div>
              <div className="col" />
            </div>
          </Tab>
          <Tab eventKey="measure" title="Measure">
            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">MEASURE</h6>
            <div className="row">
              <div className="col">
                <FormGroup name="focusX" type="number" label="Vanishing Point X" />
              </div>
              <div className="col">
                <FormGroup name="focusY" type="number" label="Vanishing Point Y" />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="cameraHeight" type="number" label="Camera Height" />
              </div>
              <div className="col">
                <FormGroup name="refDistance" type="number" label="Reference Distance" />
              </div>
              <div className="col">
                <FormGroup name="refY" type="number" label="Reference Y" />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <label>Vanishing Point</label>
                <VanishingPointView values={values} setValues={setValues} />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <label>Reference Line</label>
                <ReferenceView values={values} setValues={setValues} />
              </div>
            </div>
          </Tab>
        </Tabs>
      )}
    </FormResource>
  );
};

export default FormArScene;
